import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check, Building07 } from '@untitled-ui/icons-react'

import { useDashboard } from '../Context'

function CompanySwitcher() {
  const { companies, companyId, activeCompany, setCompanyId } = useDashboard()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  // Close on outside click. Mirrors how other native popovers in this codebase
  // handle dismissal — no portal needed since the menu sits inside the header.
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Single-company users: render the name as a plain label, no chevron — the
  // dropdown would just be visual noise when there's nothing to switch to.
  if (!companies || companies.length === 0) {
    return null
  }
  if (companies.length === 1) {
    return (
      <span className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm">
        <Building07 className="h-4 w-4 text-gray-400" />
        {activeCompany?.name || companies[0].name}
      </span>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand/500"
      >
        <Building07 className="h-4 w-4 text-gray-400" />
        <span className="max-w-[260px] truncate">
          {activeCompany?.name || 'Select company'}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 w-72 origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
          <ul className="max-h-72 overflow-y-auto">
            {companies.map((c) => {
              const selected = c.id === companyId
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyId(c.id)
                      setOpen(false)
                    }}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-gray-50 ${
                      selected ? 'text-brand-700' : 'text-gray-700'
                    }`}
                  >
                    <span className="truncate">{c.name}</span>
                    {selected && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

function DashboardHeader() {
  const { overview, activeCompany } = useDashboard()
  const name = overview?.name || activeCompany?.name || '—'

  return (
    <div className="flex flex-col gap-3 w-full p-8 pb-0">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-semibold text-[#181d27]">{name}</h1>
          <p className="text-[16px] text-[#535862]">
            Manage your team members and their account permissions here.
          </p>
        </div>

        <div className="shrink-0">
          <CompanySwitcher />
        </div>
      </div>
    </div>
  )
}

export default DashboardHeader
